/**
 * Phase 3 — Public Claim Page (/claim/:token).
 * Anyone can peek. Redeem may require login depending on claim.requires_holder_login.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Clock, Lock, XCircle, Gift as GiftIcon } from 'lucide-react';
import theme from '../theme';
import { ClaimLifecycle, ClaimNavButtons } from '../components/ClaimLifecycle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNAVAILABLE_COPY = {
  expired: 'This claim link has expired. Contact the creator if you believe you should still have access.',
  exhausted: 'All available claims for this link have already been used.',
  revoked: 'The creator has turned off this claim link.',
  paused: 'The creator has paused this claim link. Please check back later.',
};

export default function PublicClaimPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [claim, setClaim] = useState(null);
  const [err, setErr] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState(null);
  const [myClaim, setMyClaim] = useState(null);

  const authHeader = useMemo(() => {
    const t = localStorage.getItem('token');
    return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
  }, []);
  const isLoggedIn = !!localStorage.getItem('token');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/claim/${token}`, authHeader);
        setClaim(res.data?.claim || null);
        setMyClaim(res.data?.my_claim || null);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Claim link not found');
      } finally { setLoading(false); }
    })();
  }, [token, authHeader]);

  const redeem = async () => {
    setRedeeming(true);
    try {
      const res = await axios.post(`${API}/claim/${token}/redeem`, {}, authHeader);
      setResult(res.data);
      if (res.data?.duplicate) toast.info('You already claimed this Kickback');
      else toast.success('Kickback claimed');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Redeem failed';
      toast.error(msg);
      setResult({ success: false, message: msg });
    } finally { setRedeeming(false); }
  };

  const wrapStyle = {
    minHeight: '100vh', background: theme.colors.bg.secondary,
    padding: '40px 20px', display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
  };
  const cardStyle = {
    width: '100%', maxWidth: 640, background: theme.colors.bg.card,
    border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card,
    boxShadow: theme.colors.shadows.md, padding: 28,
  };

  if (loading) {
    return <div data-testid="claim-page-loading" style={wrapStyle}><div style={cardStyle}>Loading claim…</div></div>;
  }
  if (err || !claim) {
    return (
      <div style={wrapStyle}>
        <div data-testid="claim-page-error" style={cardStyle}>
          <XCircle size={28} color={theme.colors.status.error} />
          <div style={{ fontSize: 20, fontWeight: 800, marginTop: 8, color: theme.colors.text.primary }}>Claim unavailable</div>
          <div style={{ color: theme.colors.text.muted, marginTop: 8 }}>{err || 'Not found'}</div>
          <div style={{ color: theme.colors.text.muted, marginTop: 6, fontSize: 13 }}>
            Check that you copied the full link, or ask the creator for a new one.
          </div>
          <ClaimNavButtons isLoggedIn={isLoggedIn} />
        </div>
      </div>
    );
  }

  const statusPill = () => {
    const color = claim.available ? theme.colors.status.success : theme.colors.text.muted;
    const label = claim.status?.toUpperCase();
    return (
      <span data-testid="claim-status-pill" style={{ padding: '4px 10px', borderRadius: 999,
        background: `${color}1A`, color, fontWeight: 800, fontSize: 11 }}>{label}</span>
    );
  };

  return (
    <div style={wrapStyle}>
      <div data-testid="claim-page" style={cardStyle}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
          <GiftIcon size={26} color={theme.colors.primary} />
          <div style={{ fontSize: 22, fontWeight: 900, color: theme.colors.text.primary, flex: 1 }}
            data-testid="claim-title">{claim.title}</div>
          {statusPill()}
        </div>
        <div style={{ fontSize: 13, color: theme.colors.text.muted, marginBottom: 12 }}>
          From <b data-testid="claim-creator-name">{claim.creator_name || 'Creator'}</b>
          {claim.portfolio_name && <> · Kickback: <b>{claim.portfolio_name}</b></>}
          {claim.claim_type && <> · Type: <b>{claim.claim_type}</b></>}
        </div>

        {claim.description && (
          <div data-testid="claim-description" style={{ padding: 14, background: theme.colors.bg.secondary,
            borderRadius: theme.radius.sm, color: theme.colors.text.secondary, marginBottom: 14 }}>
            {claim.description}
          </div>
        )}

        <div style={{ padding: 12, background: '#FFF7ED', border: '1px solid #FED7AA',
          borderRadius: theme.radius.sm, marginBottom: 16, fontSize: 12, color: '#7C2D12' }}>
          <b>Note:</b> {claim.benefits_notice}
        </div>

        {claim.expires_at && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
            color: theme.colors.text.muted, marginBottom: 10 }}>
            <Clock size={13} /> Expires: {new Date(claim.expires_at).toLocaleString()}
          </div>
        )}

        {myClaim && !result && (
          <div data-testid="claim-already-yours" style={{ padding: 14, background: '#ECFDF5',
            border: '1px solid #A7F3D0', borderRadius: theme.radius.sm, marginBottom: 12 }}>
            <div style={{ fontWeight: 800, color: '#065F46', marginBottom: 10 }}>You already claimed this Kickback</div>
            <ClaimLifecycle claimedAt={myClaim.claimed_at} entitlementStatus={myClaim.entitlement_status}
              issuance={myClaim.issuance} testidPrefix="claim-mine" />
            <ClaimNavButtons isLoggedIn />
          </div>
        )}

        {!claim.available && !myClaim && (
          <div data-testid="claim-unavailable" style={{ padding: 12, background: '#FEF2F2',
            border: '1px solid #FECACA', borderRadius: theme.radius.sm, color: '#991B1B', marginBottom: 12 }}>
            <div style={{ fontWeight: 800 }}>This claim link is no longer available</div>
            <div data-testid="claim-unavailable-reason" style={{ fontSize: 13, marginTop: 4 }}>
              {UNAVAILABLE_COPY[claim.status] || `Status: ${claim.status}`}
            </div>
            <ClaimNavButtons isLoggedIn={isLoggedIn} next={`/claim/${token}`} />
          </div>
        )}

        {claim.available && claim.requires_holder_login && !isLoggedIn && (
          <div data-testid="claim-login-required" style={{ padding: 12, background: '#EFF6FF',
            border: '1px solid #BFDBFE', borderRadius: theme.radius.sm, color: '#1E40AF',
            marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Lock size={14} />
            <div style={{ flex: 1 }}>You must sign in to claim this Kickback.</div>
            <button data-testid="claim-login-btn" onClick={() => navigate(`/auth?next=/claim/${token}`)}
              style={{ padding: '6px 12px', borderRadius: theme.radius.sm, border: 'none',
                background: theme.colors.primary, color: '#fff', cursor: 'pointer', fontWeight: 700 }}>
              Sign in
            </button>
          </div>
        )}

        {claim.available && !myClaim && (!claim.requires_holder_login || isLoggedIn) && !result && (
          <button data-testid="claim-redeem-btn" onClick={redeem} disabled={redeeming}
            style={{ width: '100%', padding: '14px 20px', borderRadius: theme.radius.sm, border: 'none',
              background: theme.colors.status.success, color: '#fff', fontWeight: 800, fontSize: 15,
              cursor: 'pointer' }}>
            {redeeming ? 'Redeeming…' : 'Claim / Redeem this Kickback'}
          </button>
        )}

        {result && result.success && (
          <div data-testid="claim-success" style={{ marginTop: 14, padding: 14,
            background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: theme.radius.sm, color: '#065F46' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800 }}>
              <CheckCircle2 size={16} /> {result.duplicate ? 'Already claimed' : 'Claim recorded'}
            </div>
            <div style={{ marginTop: 6, marginBottom: 12, fontSize: 12 }}>{result.message}</div>
            <ClaimLifecycle claimedAt={result.redemption?.redeemed_at}
              entitlementStatus={result.entitlement?.status} issuance={result.issuance} testidPrefix="claim-result" />
            <ClaimNavButtons isLoggedIn={isLoggedIn} />
          </div>
        )}

        {result && !result.success && (
          <div data-testid="claim-failed" style={{ marginTop: 14, padding: 14, background: '#FEF2F2',
            border: '1px solid #FECACA', borderRadius: theme.radius.sm, color: '#991B1B' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800 }}>
              <XCircle size={16} /> Claim not completed
            </div>
            <div data-testid="claim-failed-reason" style={{ marginTop: 6, fontSize: 13 }}>{result.message}</div>
            <div style={{ marginTop: 4, fontSize: 12 }}>No entitlement was saved and nothing was issued.</div>
            <button data-testid="claim-retry-btn" onClick={() => setResult(null)}
              style={{ marginTop: 10, padding: '8px 14px', borderRadius: 8, border: '1px solid #991B1B',
                background: 'transparent', color: '#991B1B', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
              Try again
            </button>
            <ClaimNavButtons isLoggedIn={isLoggedIn} next={`/claim/${token}`} />
          </div>
        )}
      </div>
    </div>
  );
}
